import { Action, createReducer, on } from '@ngrx/store';
import { ConfigPanelMode } from '../../enums/config';
import * as ConfigActions from './config.actions';
import { ViewRepresentation } from '../../enums/representation';
import { SortableListKey, SortConfig, SortDirection } from '../../enums/sort';

export interface State {
  leftPanelMode: ConfigPanelMode;
  rightPanelMode: ConfigPanelMode;
  displaySitesFilters: boolean,
  sitesFilters: any,
  colorRampPositiveColors: Array<string>;
  colorRampNegativeColors: Array<string> ;
  colorRampMin: number;
  colorRampMax: number;
  siteDetailSelectedSection: number | undefined;
  searchWorldText: string | undefined;
  tilesetStyleColorConditions: Array<any> | undefined;
  tilesetColorRampMin: number;
  tilesetColorRampMax: number;
  viewRepresentation: number;
  tilesetIsLoading: boolean;
  splittedTilesetIsLoading: boolean;
  pointSize: number;
  isDownloadingPicture: boolean
  displayDetections: boolean
  displayVolumes: boolean
  displayMeasures: boolean
  displayCuts: boolean
  displayElevations: boolean
  displayMarkers: boolean,
  itemDetailExpanded: boolean,
  sortConfigs: Record<SortableListKey, SortConfig>;
  // rightSideScreen: ConfigRightSideScreen | undefined,
}

export const initialState: State = {
  leftPanelMode: ConfigPanelMode.MODE_EXPANDED,
  rightPanelMode: ConfigPanelMode.MODE_HIDDEN,
  displaySitesFilters: false,
  sitesFilters: undefined,
  colorRampPositiveColors: [],
  colorRampNegativeColors: [],
  colorRampMin: 0,
  colorRampMax: 0,
  siteDetailSelectedSection: undefined,
  searchWorldText: undefined,
  tilesetStyleColorConditions: undefined,
  tilesetColorRampMin: 0,
  tilesetColorRampMax: 0,
  viewRepresentation: ViewRepresentation.TEXTURED,
  tilesetIsLoading: false,
  splittedTilesetIsLoading: false,
  pointSize: 1,
  isDownloadingPicture: false,
  displayDetections: true,
  displayVolumes: true,
  displayMeasures: true,
  displayCuts: true,
  displayElevations: true,
  displayMarkers: true,
  itemDetailExpanded: false,
  sortConfigs: {
    [SortableListKey.MEASURES]: { field: 'name', direction: SortDirection.NONE },
    [SortableListKey.CUTS]: { field: 'name', direction: SortDirection.NONE },
    [SortableListKey.ELEVATIONS]: { field: 'name', direction: SortDirection.NONE },
    [SortableListKey.MARKERS]: { field: 'name', direction: SortDirection.NONE },
    [SortableListKey.VOLUMES]: { field: 'volume', direction: SortDirection.NONE },
    [SortableListKey.DETECTIONS]: { field: 'confidence', direction: SortDirection.NONE },
  }
};


const configReducer = createReducer(
  initialState,
  on(ConfigActions.setConfigLeftPanelMode, (state, { mode }) => {
    return { ...state, leftPanelMode: mode };
  }),
  on(ConfigActions.setConfigRightPanelMode, (state, { mode }) => {
    return { ...state, rightPanelMode: mode };
  }),
  on(ConfigActions.setConfigDisplaySitesFilters, (state, { display }) => {
    return { ...state, displaySitesFilters: display };
  }),
  on(ConfigActions.setConfigSitesFilters, (state, { sitesFilters }) => {
    return { ...state, sitesFilters: sitesFilters };
  }),
  on(ConfigActions.setConfigColorRampPositiveColors, (state, { colors }) => {
    return { ...state, colorRampPositiveColors: colors };
  }),
  on(ConfigActions.setConfigColorRampNegativeColors, (state, { colors }) => {
    return { ...state, colorRampNegativeColors: colors };
  }),
  on(ConfigActions.setConfigColorRampMin, (state, { value }) => {
    return { ...state, colorRampMin: value };
  }),
  on(ConfigActions.setConfigColorRampMax, (state, { value }) => {
    return { ...state, colorRampMax: value };
  }),
  on(ConfigActions.setConfigSiteDetailSelectedSection, (state, { value }) => {
    return { ...state, siteDetailSelectedSection: value };
  }),
  on(ConfigActions.setConfigSearchWorldText, (state, { text }) => {
    return { ...state, searchWorldText: text };
  }),
  on(ConfigActions.setConfigTilesetStyleColorConditions, (state, { conditions }) => {
    return { ...state, tilesetStyleColorConditions: conditions };
  }),
  on(ConfigActions.setConfigTilesetColorRampMin, (state, { value }) => {
    return { ...state, tilesetColorRampMin: value };
  }),
  on(ConfigActions.setConfigTilesetColorRampMax, (state, { value }) => {
    return { ...state, tilesetColorRampMax: value };
  }),
  on(ConfigActions.setConfigViewRepresentation, (state, { value }) => {
    return { ...state, viewRepresentation: value };
  }),
  on(ConfigActions.setConfigTilesetIsLoading, (state, { isLoading }) => {
    return { ...state, tilesetIsLoading: isLoading };
  }),
  on(ConfigActions.setConfigSplittedTilesetIsLoading, (state, { isLoading }) => {
    return { ...state, splittedTilesetIsLoading: isLoading };
  }),
  on(ConfigActions.setConfigPointCloudPointSize, (state, { pointSize }) => {
    if (pointSize) {
      return { ...state, pointSize: pointSize };
    }
    return state;
  }),
  on(ConfigActions.setConfigIsDownloadingAlteredPicture, (state, { isDownloading }) => {
    return { ...state, isDownloadingPicture: isDownloading };
  }),
  on(ConfigActions.setConfigDisplayMeasures, (state, { display }) => {
    return { ...state, displayMeasures: display };
  }),
  on(ConfigActions.setConfigDisplayCuts, (state, { display }) => {
    return { ...state, displayCuts: display };
  }),
  on(ConfigActions.setConfigDisplayElevations, (state, { display }) => {
    return { ...state, displayElevations: display };
  }),
  on(ConfigActions.setConfigDisplayMarkers, (state, { display }) => {
    return { ...state, displayMarkers: display };
  }),
  on(ConfigActions.setConfigDisplayDetections, (state, { display }) => {
    return { ...state, displayDetections: display };
  }),
  on(ConfigActions.setConfigDisplayVolumes, (state, { display }) => {
    return { ...state, displayVolumes: display };
  }),
  on(ConfigActions.setConfigExpandItemDetail, (state, { expand }) => {
    return { ...state, itemDetailExpanded: expand };
  }),
  on(ConfigActions.toggleSort, (state, { list, field }) => {
    const current = state.sortConfigs[list];
    let direction: SortDirection;
    if (current.field !== field) {
      direction = SortDirection.ASC;
    } else {
      direction = nextDirection(current.direction);
    }

    return {
      ...state,
      sortConfigs: {
        ...state.sortConfigs,
        [list]: {
          field: direction === SortDirection.NONE ? null : field,
          direction
        }
      }
    };
  }),
);

export function reducer(state: State | undefined, action: Action): any {
  return configReducer(state, action);
}

function nextDirection(direction: SortDirection): SortDirection {
  switch (direction) {
    case SortDirection.NONE:
      return SortDirection.ASC;

    case SortDirection.ASC:
      return SortDirection.DESC;

    case SortDirection.DESC:
    default:
      return SortDirection.NONE;
  }
}
